import { Button } from '@/components/ui/Button';
import type { CoachFeedbackCollectionResponse } from '@/lib/types';

/** The coach collection's row union and the compact per-player row it
 * renders inside the roster PlayerModal. Lives apart from
 * `CanonicalFeedbackCollection` so the athlete-first view (#908 §5.2) can
 * reuse the exact same row without an import cycle. */
export type CoachCollectionRow = CoachFeedbackCollectionResponse['items'][number];
export type CoachConversationRow = Extract<CoachCollectionRow, { row_kind: 'feedback' }>;
const quickLabels = { got_it: 'Got it', need_help: 'Need help', tried_this: 'Tried this' };
export function latestReply(row: CoachConversationRow) {
  return row.latest_message?.text || (row.latest_message?.quick_label ? quickLabels[row.latest_message.quick_label] : 'Coach shared Feedback');
}
export function conversationReason(row: CoachConversationRow) {
  const reason = row.attention.reason;
  return reason === 'help' ? 'Need help' : reason === 'reopening_requested' ? 'Reopening requested'
    : reason === 'written_reply' ? 'Reply needed' : reason === 'unread' ? 'Unread reply'
    : row.closed ? 'Closed' : row.attention.next_responder === 'player' ? 'Awaiting player' : 'No reply needed';
}

/** One compact coach row. Without `onOpen` the row is read-only (the
 * athlete-first view lists teams outside the active scope that way) and
 * `openHint` says what would make it openable. */
export function CoachFeedbackHistoryRow({ row, onOpen, scopeLabel, openHint }: {
  row: CoachCollectionRow; onOpen?(row: CoachCollectionRow): void; scopeLabel?: string; openHint?: string;
}) {
  const title = row.message_excerpt || 'Private draft';
  const state = row.row_kind === 'feedback' ? `${row.closed ? 'Closed' : 'Open'} · ${conversationReason(row)}` : row.row_kind === 'draft' ? 'Private draft' : 'Coaches only';
  return <div className="player-feedback-history-row" data-feedback-id={row.feedback_id} data-player-id={row.row_kind === 'feedback' ? row.player_id : undefined} data-item-kind={row.row_kind}>
    <div>{onOpen ? <button className="canonical-heading" onClick={() => onOpen(row)}>{title}</button> : <span className="canonical-heading">{title}</span>}<p className="text-sm text-ink-muted">{scopeLabel ? `${scopeLabel} · ${state}` : state}</p></div>
    {onOpen ? <Button variant="ghost" onClick={() => onOpen(row)}>{row.row_kind === 'draft' ? 'Resume' : 'Open'}</Button> : openHint ? <small className="text-xs text-ink-muted">{openHint}</small> : null}
  </div>;
}
